import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/config/site';

/**
 * Build a locale-prefixed pathname.
 *
 * Convention: the tenant's default locale serves bare URLs (no prefix) so
 * existing inbound links and Google rankings stay intact. Only non-default
 * locales get a `/{locale}/...` prefix.
 *
 * @param path  An app pathname (e.g., "/shop", "/product/red-tomato"). Leading
 *              slash optional. Absolute URLs (with scheme) are returned as-is
 *              so callers don't accidentally rewrite outbound links.
 * @param locale Target locale. Defaults to the tenant's default.
 */
export function localizedPath(path: string, locale: string = DEFAULT_LOCALE): string {
  if (/^[a-z]+:\/\//i.test(path) || path.startsWith('//')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return normalized;
  if (!SUPPORTED_LOCALES.includes(locale)) return normalized;
  return `/${locale}${normalized === '/' ? '' : normalized}`;
}

/**
 * Inverse of `localizedPath`: read a pathname and return its
 * `{ locale, path }` parts. Useful for the locale switcher when computing
 * the equivalent URL in another language for static (non-slug) routes.
 */
export function parseLocalizedPath(pathname: string): { locale: string; path: string } {
  const m = pathname.match(/^\/([a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*)(\/.*|$)/i);
  if (m && SUPPORTED_LOCALES.includes(m[1]) && m[1] !== DEFAULT_LOCALE) {
    return { locale: m[1], path: m[2] || '/' };
  }
  return { locale: DEFAULT_LOCALE, path: pathname };
}
